import { UserAdminRoles } from '@ms/yammer-graphql';

import {
  AppViewPropertyKey,
  DefaultAppViewPropertyId,
  RecurrentDismissableAppViewPropertyTypes,
  RoleBasedFREAppViewProperty,
  defaultAppViewProperties,
} from '../../state/appViewProperties/types';
import { YammerState } from '../../state/types';
import { getAppViewPropertiesStatus, getAppViewProperty } from '../appViewProperties/selectors';

type GetRoleBasedFREData = (
  state: YammerState,
  appViewPropertyKey: RecurrentDismissableAppViewPropertyTypes
) => RoleBasedFREAppViewProperty;
export const getRoleBasedFREData: GetRoleBasedFREData = (state, appViewPropertyKey) => {
  const defaultValue = defaultAppViewProperties[appViewPropertyKey] as RoleBasedFREAppViewProperty;

  const requestStatus = getAppViewPropertiesStatus(state, DefaultAppViewPropertyId, [appViewPropertyKey]);
  const isDataLoaded = requestStatus === 'fulfilled';

  const appViewPropertiesValue = getAppViewProperty(state, DefaultAppViewPropertyId, appViewPropertyKey);
  const isDataSet = !!appViewPropertiesValue;

  if (!isDataLoaded || !isDataSet) {
    return defaultValue;
  }

  return appViewPropertiesValue as RoleBasedFREAppViewProperty;
};

type ShouldDisplaySettingsTeachingBubble = (state: YammerState) => boolean;
export const shouldDisplaySettingsTeachingBubble: ShouldDisplaySettingsTeachingBubble = (state) =>
  Boolean(state.roleBasedFREs.fulfilled?.showSettingsTeachingBubbleForRoleBasedFRE);

type isTenantSetUp = (state: YammerState) => boolean;
export const isTenantSetUp: isTenantSetUp = (state) => {
  const data = state.roleBasedFREs.fulfilled;
  const allLeadersHaveAudience = !!data?.allLeadersHaveAudience;
  const numberOfLeaders = data?.numberOfLeaders || 0;
  const numberOfCorporateCommunicators = data?.numberOfCorporateCommunicators || 0;
  const hasEnoughCorporateCommunicators = numberOfCorporateCommunicators >= 3;
  const hasEnoughLeaders = numberOfLeaders >= 3;

  return allLeadersHaveAudience && hasEnoughCorporateCommunicators && hasEnoughLeaders;
};

type ShouldDismissImmediately = (
  state: YammerState,
  appViewPropertyKey: RecurrentDismissableAppViewPropertyTypes,
  dismissCount: number
) => boolean;
const shouldDismissImmediately: ShouldDismissImmediately = (state, appViewPropertyKey, dismissCount) => {
  switch (appViewPropertyKey) {
    case AppViewPropertyKey.RoleBasedFRE:
      return isTenantSetUp(state) && dismissCount >= 1;
    case AppViewPropertyKey.M365CopilotFRE:
      return dismissCount >= 3;
    default:
      return false;
  }
};

type IsPermanentDismissal = (
  state: YammerState,
  appViewPropertyKey: RecurrentDismissableAppViewPropertyTypes
) => boolean;
export const isPermanentDismissal: IsPermanentDismissal = (state, appViewPropertyKey) => {
  const propertyValue = getRoleBasedFREData(state, appViewPropertyKey);
  const newDismissCount = propertyValue.dismissCount + 1;
  const isEarlyDismissal = shouldDismissImmediately(state, appViewPropertyKey, newDismissCount);

  const isLateDismissal = newDismissCount >= 3;

  return isEarlyDismissal || isLateDismissal;
};

type GetViewerAdminRoles = (state: YammerState) => UserAdminRoles[];
export const getViewerAdminRoles: GetViewerAdminRoles = (state: YammerState) =>
  state.roleBasedFREs.fulfilled?.viewerRoles || [];

export const hasError = (state: YammerState) => typeof state.roleBasedFREs.rejected !== 'undefined';
